import { useTable } from "react-table";
import { useMemo, useState } from "react";
import { defaultDateDisplay, formatCurrency } from "../../../helpers";
import { useSelector } from "react-redux";
import Icon from "../../../assets/icons";
import styles from "./styles.module.scss";

const NoteContent = ({ note }) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div style={{ position: "relative" }}>
      <div
        onMouseEnter={() => setShowDetails(true)}
        onMouseLeave={() => setShowDetails(false)}
        className={styles.noteIconContainer}
      >
        {showDetails ? (
          <img src={Icon.noteTwo} alt="" />
        ) : (
          <img src={Icon.noteOne} alt="" />
        )}
      </div>
      {showDetails && (
        <div
          onMouseEnter={() => setShowDetails(true)}
          onMouseLeave={() => setShowDetails(false)}
          className={styles.noteDetails}
        >
          {note}
        </div>
      )}
    </div>
  );
};

const useCustomerCreditLogTable = ({ credits }) => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedRefs, setSelectedRefs] = useState(null);
  const { storeList } = useSelector((state) => state.profileDetailsReducer);

  const columns = useMemo(
    () => [
      {
        Header: "Date/Time",
        accessor: "created_at",
        Cell: (cell) => defaultDateDisplay(cell?.value),
      },
      {
        Header: "Reference",
        accessor: "reference",
        Cell: (cell) =>
          cell?.value ? (
            <span
              style={{
                color: "#7647EE",
                height: "28px",
                cursor: "pointer",
              }}
              role="button"
              onClick={() => {
                setSelectedRefs(cell?.value);
              }}
            >
              {cell?.value}
            </span>
          ) : (
            "_____"
          ),
      },
      {
        Header: "Payment Method",
        accessor: "repayment_method",
        Cell: (cell) => <div>{cell?.value ? cell?.value : "____"}</div>,
      },
      {
        Header: "Description",
        accessor: "is_manually_added",
        Cell: ({ cell, row }) => (
          <div style={{ display: "flex" }}>
            <div className={styles.decription}>
              <p>
                {row?.original?.type === "payment"
                  ? "Payment"
                  : row?.original?.type === "write-off"
                    ? "Write off"
                    : cell.value
                      ? "Debt initiated in Debtors"
                      : "Debt initiated in Sell Mode"}
              </p>
            </div>
          </div>
        ),
      },
      {
        Header: "Amount",
        accessor: "amount",
        Cell: ({ cell, row }) => {
          const currencyCode = storeList?.find(
            (storeItem) => `${storeItem?.id}` === `${row?.original?.store_id}`,
          )?.currency_code;

          const val = formatCurrency({
            value: cell.value,
            currencyCode,
          });

          const colorMap = {
            credit: "#E02020",
            "write-off": "#f7ad3c",
            default: "#19AE57",
          };

          const color = colorMap[row?.original?.type] || colorMap.default;

          const isNegative = ["credit"].includes(row?.original?.type);
          const displayValue = isNegative ? `-${val}` : val;

          return <span style={{ color }}>{displayValue}</span>;
        },
      },
      {
        Header: "Notes",
        accessor: "note",
        Cell: ({ cell }) =>
          cell?.value ? <NoteContent note={cell?.value} /> : "N/A",
      },
      {
        Header: "Balance",
        accessor: "closing_outstanding_balance",
        Cell: ({ cell, row }) => {
          return (
            <span
              style={{
                color: cell?.value <= 0 ? "#19AE57" : "#E02020",
              }}
            >
              {formatCurrency({
                value:
                  cell?.value <= 0
                    ? Math.abs(cell?.value)
                    : -Math.abs(cell?.value),
                currencyCode: storeList?.find(
                  (storeItem) =>
                    `${storeItem?.id}` === `${row?.original?.store_id}`,
                )?.currency_code,
              })}
            </span>
          );
        },
      },
      {
        Header: "Transaction Date",
        accessor: "repaid_at",
        Cell: ({ cell, row }) =>
          cell.value
            ? defaultDateDisplay(cell.value)
            : defaultDateDisplay(row?.original?.written_off_at),
      },
      {
        Header: "Due Date",
        accessor: "expected_repayment_date",
        Cell: (cell) => (cell.value ? defaultDateDisplay(cell.value) : "___"),
      },
      {
        Header: "Cashier",
        accessor: "cashier_name",
      },
      {
        Header: "Store",
        accessor: "store_name",
      },
    ],
    [],
  );

  const instance = useTable({ columns, data: credits });

  return {
    instance,
    selectedOrder,
    setSelectedOrder,
    selectedRefs,
    setSelectedRefs,
  };
};

export default useCustomerCreditLogTable;
